import { effect, inject, Injectable } from '@angular/core';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { IqMcModalEventService } from '@dat/iq-mc-modal';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { get, isEmpty, isNil, kebabCase, startCase, toLower } from 'lodash';
import { Observable, take, tap } from 'rxjs';
import { IqMcEventBusPluginGraphqlService } from './iq-mc-event-bus-plugin-graphql.service';
import { IqMcEventBusStateAreaService } from './iq-mc-event-bus-state-area.service';
import {
    createMcEventNames,
    fetchKeys,
    GeoType,
    GeoTypesSchema,
    McLocation,
    McLocationQuery,
    McLocationQuerySchema,
    McQuery,
    McQuerySchema,
    RouteParams,
    TimeframeControls,
    TimeFrames,
} from './iq-mc-event-bus-state.models';
import { DatiqUserStateService, DatiqUserTiers } from '@dat/datiq-user';
import { DateTime } from 'luxon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type State = {
    locations: Record<string, McLocation>;
    locationQueries: Record<string, McLocationQuery>;
    queries: Record<string, McQuery>;
};

@Injectable({
    providedIn: 'root',
})
export class IqMcEventBusStateService extends ComponentStore<State> {
    private userTier: DatiqUserTiers | undefined;
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _plugin: IqMcEventBusPluginGraphqlService = inject(
        IqMcEventBusPluginGraphqlService,
    );
    private _areaStore: IqMcEventBusStateAreaService = inject(
        IqMcEventBusStateAreaService,
    );
    private _modalService: IqMcModalEventService = inject(
        IqMcModalEventService,
    );
    private _userService: DatiqUserStateService = inject(DatiqUserStateService);

    constructor() {
        super({ locations: {}, queries: {}, locationQueries: {} });

        /**
         * Log the state of the store when it changes
         * This is used for debugging and monitoring
         */
        // effect(() => {
        //     console.log('MC State');
        //     console.log(this.state());
        // });

        this._userService
            .getUserLoggedInTier$()
            .pipe(
                take(1),
                tap((tier) => {
                    this.userTier = tier;
                }),
                takeUntilDestroyed(),
            )
            .subscribe();

        this._bus.registerMap({
            'type:query': {
                callback: ({ query }: { query: McLocationQuery }) => {
                    if (!query) return;
                    this.checkQuery(query);
                },
            },
            'type:query:cache': {
                callback: ({ query }: { query: McLocationQuery }) => {
                    if (!query) return;
                    this.checkQuery(query);
                },
            },
            'type:geo:location:query:area': {
                callback: ({ area, id }: { id: string; area: GeoType }) => {
                    if (!area || !id) return;
                    this.checkArea({ area, id });
                },
            },
            'type:dispatch:gauge:labels': {
                callback: ({ query }: { query: McQuery }) => {
                    if (!query) return;
                    this.dispatchGaugeLabels(query);
                },
            },
            'type:clear:mc:queries': {
                callback: (payload: { id: string }[]) => {
                    payload.forEach(({ id }) => {
                        this.removeQuery(id);
                    });
                },
            },
            'type:query:update': {
                callback: ({ query }: { query: Partial<McQuery> }) => {
                    if (!query) return;
                    this.checkQueryUpdate(query);
                },
            },
        });
    }

    //#region Selectors

    getLocationByQuery = (query: McLocationQuery) =>
        this.selectSignal(() => {
            return this.state().locations[kebabCase(query.displayName)];
        });
    getLocationByQueryDisplayName = (displayName: string) =>
        this.selectSignal(() => {
            return this.state().locations[kebabCase(displayName)];
        });

    addLocationQuery = this.updater((state: State, query: McLocationQuery) => {
        const id = query?.id ?? undefined;
        return !!id
            ? {
                  ...state,
                  locationQueries: {
                      ...state.locationQueries,
                      [query.id]: query,
                  },
              }
            : state;
    });

    getLocationQueryById = (id: string) =>
        this.selectSignal((state) => {
            return state.locationQueries[id];
        });

    getQueryById = (id: string) =>
        this.selectSignal((state) => {
            return state.queries[id];
        });

    isLocationFetched = (displayName: string) =>
        this.selectSignal((state: State): boolean => {
            return state.locations[kebabCase(displayName)]?.fetch || false;
        });

    getGeoCode = (displayName: string, geoType: string) =>
        this.selectSignal(() => {
            const fetchKey = kebabCase(displayName);
            return (
                this.state().locations[fetchKey]?.geoCodes?.[geoType]?.[0]
                    ?.code ?? ''
            );
        });

    //#endregion

    //#region Updaters

    /**
     * Sets the location to fetch in the state
     *
     * This is so we don't call a location twice.
     */
    setLocationToFetch = this.updater((state: State, displayName: string) => {
        return {
            ...state,
            locations: {
                ...state.locations,
                [kebabCase(displayName)]: {
                    displayName,
                    fetch: true,
                    fetchInMs: Date.now(),
                },
            },
        };
    });

    addQuery = this.updater((state: State, query: McQuery) => {
        const id = query?.id ?? undefined;
        return !!id
            ? {
                  ...state,
                  queries: {
                      ...state.queries,
                      [id]: {
                          ...query,
                      },
                  },
              }
            : state;
    });

    patchQuery = this.updater((state: State, query: Partial<McQuery>) => {
        const id = query?.id ?? undefined;
        const currentQuery = get(state, ['queries', id], {});
        return !!id
            ? {
                  ...state,
                  queries: {
                      ...state.queries,
                      [id]: {
                          ...currentQuery,
                          ...query,
                      },
                  },
              }
            : state;
    });

    removeQuery = this.updater((state: State, queryId: string) => {
        const { [queryId]: removedQuery, ...remainingQueries } = state.queries;
        return {
            ...state,
            queries: remainingQueries,
        };
    });

    addLocation = this.updater((state: State, location: McLocation) => {
        const currentLocation =
            state.locations[kebabCase(location.displayName)];
        const loadedInMs = currentLocation?.loadedInMs || Date.now();
        return {
            ...state,
            locations: {
                ...state.locations,
                [kebabCase(location.displayName)]: {
                    ...currentLocation,
                    ...location,
                    loadedInMs,
                    loaded: true,
                    fetch: true,
                    duration: loadedInMs - currentLocation?.fetchInMs,
                },
            },
        };
    });

    updateLocationArea = this.updater(
        (state: State, { id, area }: { id: string; area: GeoType }) => {
            const currentQuery = state.locationQueries[id] ?? {};

            return {
                ...state,
                locationQueries: {
                    ...state.locationQueries,
                    [id]: {
                        ...currentQuery,
                        area,
                    },
                },
            };
        },
    );

    //#endregion
    //#region Effects

    fetchLocation = this.effect((query$: Observable<McLocationQuery>) => {
        return query$.pipe(
            tapResponse({
                next: async (query: McLocationQuery) => {
                    const { displayName } = query;
                    if (!displayName) return;
                    if (this.isLocationFetched(displayName)()) return;

                    this.setLocationToFetch(displayName);
                    const location: McLocation = await this._plugin.getLocation(
                        {
                            search: displayName,
                        },
                    );

                    this.addLocation(location);
                    this.sendLocationEvent(query);
                },
                error: (e) => {
                    console.error(e);
                },
            }),
        );
    });
    fetchLocationById = this.effect((id$: Observable<string>) => {
        return id$.pipe(
            tapResponse({
                next: async (id: string) => {
                    const query = this.getLocationQueryById(id)();

                    this.sendLocationEvent(query);
                },
                error: (e) => {
                    console.error(e);
                },
            }),
        );
    });

    initialize = this.effect(($trigger) => {
        return $trigger.pipe(
            tapResponse({
                next: () => {
                    // do something
                },
                error: () => {
                    // do something
                },
            }),
        );
    });

    checkArea = this.effect(
        ($area: Observable<{ id: string; area: GeoType }>) => {
            return $area.pipe(
                tapResponse({
                    next: ({ id, area }: { id: string; area: GeoType }) => {
                        if (!area) return;

                        const checkedArea = GeoTypesSchema.parse(area);

                        this.updateLocationArea({ id, area: checkedArea });
                        this.fetchLocationById(id);
                    },
                    error: (e) => {
                        console.error(e);
                    },
                }),
            );
        },
    );

    checkQuery = this.effect(
        (query$: Observable<McLocationQuery>): Observable<McLocationQuery> => {
            return query$.pipe(
                tapResponse({
                    next: (query: McLocationQuery) => {
                        if (query.type !== 'location') return;

                        try {
                            /**
                             * Validate the query against the schema
                             *
                             * @throws {Error} If the query does not match the schema
                             */
                            const check = McLocationQuerySchema.parse(query);

                            this.addLocationQuery(check);
                            this.fetchLocation(check);
                        } catch (e) {
                            /**
                             * Log the error if the query fails the schema check
                             * This will be connected to OTEL so it can be pushed to Observe
                             */
                            console.error(e);
                        }
                    },
                    error: (e) => {
                        console.error(e);
                    },
                }),
                tapResponse({
                    next: (query: McQuery) => {
                        if (query.type !== 'area' || isNil(query.geoType))
                            return;
                        try {
                            /**
                             * Validate the query against the schema
                             *
                             * @throws {Error} If the query does not match the schema
                             */
                            if (
                                this.shouldResolveGeo(
                                    query?.displayName,
                                    query?.geoCode,
                                )
                            ) {
                                this.fetchConditionGeo(query);
                                return;
                            }
                            const check = McQuerySchema.parse({
                                ...query,
                                geoCode: isNil(query.geoCode)
                                    ? ''
                                    : String(query.geoCode),
                            });

                            this.addQuery(check);
                            this.fetchCondition(check);
                        } catch (e) {
                            /**
                             * Log the error if the query fails the schema check
                             * This will be connected to OTEL so it can be pushed to Observe
                             */
                            console.error(e);
                        }
                    },
                    error: (e) => {
                        console.error(e);
                    },
                }),
            );
        },
    );

    checkQueryUpdate = this.effect(
        (query$: Observable<McQuery>): Observable<McQuery> => {
            return query$.pipe(
                tapResponse({
                    next: (query: McQuery) => {
                        if (query.type !== 'area') return;
                        try {
                            /**
                             * Validate the query against the schema
                             *
                             * @throws {Error} If the query does not match the schema
                             */
                            const check = McQuerySchema.parse({
                                ...query,
                                geoCode: isNil(query.geoCode)
                                    ? ''
                                    : String(query.geoCode),
                            });

                            this.patchQuery(check);
                            this.fetchCondition(check);
                        } catch (e) {
                            /**
                             * Log the error if the query fails the schema check
                             * This will be connected to OTEL so it can be pushed to Observe
                             */
                            console.error(e);
                        }
                    },
                    error: (e) => {
                        console.error(e);
                    },
                }),
            );
        },
    );

    dispatchGaugeLabels(query: McQuery) {
        const showSecondaryLabel: boolean =
            !!query.geoCode || !!query.displayName;
        const marketName: string = this.getMarketName(query);
        const primaryLabel: string = this.getPrimaryLabel(
            query,
            marketName,
            showSecondaryLabel,
        );
        const secondaryLabel: string = this.getSecondaryLabel(query);

        this._bus.dispatch({
            name: 'setMcGaugeLabel',
            payload: {
                id: query.id,
                primaryLabel,
                secondaryLabel,
                showSecondaryLabel,
            },
        });
    }

    readonly fetchCondition = this.effect((queries$: Observable<McQuery>) => {
        return queries$.pipe(
            tap((query: McQuery) => {
                fetchKeys.forEach((key) => {
                    this._bus.dispatch({
                        name: createMcEventNames(key),
                        payload: { query },
                    });
                });
            }),
        );
    });

    readonly fetchConditionGeo = this.effect((query$: Observable<McQuery>) => {
        return query$.pipe(
            tapResponse({
                next: async (query: McQuery) => {
                    let geoCode: string = this.getGeoCode(
                        query.displayName,
                        query.geoType,
                    )();
                    if (isEmpty(geoCode)) {
                        this.setLocationToFetch(query.displayName);
                        const location: McLocation =
                            await this._plugin.getLocation({
                                search: query.displayName,
                            });
                        geoCode = location.geoCodes?.[query.geoType]?.[0]?.code;

                        this.addLocationQuery({
                            id: query.id,
                            type: 'location',
                            displayName: query.displayName,
                            area: query.geoType,
                            geoCode,
                        });
                        this.addLocation(location);
                    }
                    const check: McQuery = McQuerySchema.parse({
                        ...query,
                        geoCode,
                    });
                    this.addQuery(check);
                    this.fetchCondition(check);
                },
                error: (e) => {
                    console.error(e);
                },
            }),
        );
    });
    //#endregion

    //#region Dispatchers

    sendLocationEvent(query: {
        id?: string;
        type?: 'location' | 'area';
        displayName?: string;
        area?: GeoType;
        geoCode?: string;
    }) {
        const location = this.getLocationByQuery(query)();
        const geoCodes = get(location, ['geoCodes', query.area], {});
        const code = !!query?.geoCode?.length
            ? query?.geoCode
            : get(geoCodes, ['0', 'code'], '');
        const area = !!code?.length
            ? this._areaStore.getMarket(code, query.area)()
            : null;
        const payload = {
            query,
            area,
            geoCode: geoCodes,
            location,
            code,
        };

        /**
         * Debug Message
         */
        // console.log('type:geo:location:send', payload);
        this._bus.dispatch({
            name: 'type:geo:location:send',
            payload,
        });
    }
    //#endregion

    //#region Helpers
    labelisation(label: string) {
        return startCase(toLower(label));
    }

    getPrimaryLabel(
        query: McQuery,
        marketName: string,
        showSecondaryLabel: boolean,
    ) {
        return `${this.labelisation(query.direction)}bound ${
            showSecondaryLabel
                ? `- ${marketName}`
                : this.labelisation('capacity')
        }`;
    }

    getSecondaryLabel(query: McQuery) {
        return `${this.labelisation(query.equipmentType)} | ${this.labelForTimeFrame(query.timeframe)} | ${this.labelisation(query.geoType)}`;
    }

    getMarketName(query: McQuery) {
        const geoCode: string = `${query.geoCode}`;
        if (isEmpty(geoCode)) {
            return '';
        }
        const feature = this._areaStore.getMarket(geoCode, query.geoType)();
        return feature?.properties?.name ?? '';
    }

    shouldResolveGeo(displayName: string, geoCode: string) {
        return !isEmpty(displayName) && isEmpty(geoCode);
    }

    getTimeTypeControl(timeframe: TimeFrames) {
        return {
            controlName: 'timeType',
            value: TimeframeControls[timeframe],
        };
    }

    async getLocationByDisplayName(displayName: string): Promise<McLocation> {
        this.setLocationToFetch(displayName);
        const location = await this._plugin.getLocation({
            search: displayName,
        });
        this.addLocation(location);
        return location;
    }

    async getGeoCodeForQuery(query: McQuery | RouteParams) {
        if (!this.shouldResolveGeo(query.displayName, query.geoCode))
            return query.geoCode;
        const geoCode = this.getGeoCode(query.displayName, query.geoType)();
        if (isEmpty(geoCode)) {
            this.setLocationToFetch(query.displayName);
            const location = await this.getLocationByDisplayName(
                query.displayName,
            );
            return location.geoCodes?.[query.geoType]?.[0]?.code;
        }
        return geoCode;
    }

    labelForTimeFrame(timeType: TimeFrames): string {
        const { start, end } = this.getDateRangeFromTimeFrame(timeType);
        return (timeType === TimeFrames.Current || timeType === TimeFrames.PrevBusinessDay) 
            ? start 
            : `Range: ${start} - ${end}`;
    }

    getDateRangeFromTimeFrame(timeType: TimeFrames): { start: string; end: string } {
        const TIMEZONE = 'America/Los_Angeles';
        const DATE_FORMAT = 'MM/dd/yy';
        
        const now = DateTime.now().setZone(TIMEZONE);
        const yesterday = now.minus({ days: 1 });
        
        let start: DateTime;
        let end: DateTime;
        
        switch (timeType) {
            case TimeFrames.Current:
                start = now.startOf('day');
                end = now.endOf('day');
                break;

            case TimeFrames.PrevBusinessDay:
                start = yesterday.startOf('day');
                end = yesterday.endOf('day');
                break;

            case TimeFrames.EightDaysBack:
                end = yesterday.endOf('day');
                start = yesterday.minus({ days: 7 }).startOf('day');
                break;

            case TimeFrames.ThirtyDaysBack:
                end = yesterday.endOf('day');
                start = yesterday.minus({ days: 29 }).startOf('day');
                break;

            case TimeFrames.EightDayForecast:
                start = now.startOf('day');
                end = now.plus({ days: 7 }).endOf('day');
                break;
                
            default:
                start = now.startOf('day');
                end = now.endOf('day');
                break;
        }
        
        return {
            start: start.toFormat(DATE_FORMAT),
            end: end.toFormat(DATE_FORMAT)
        };
    }

    getUserTier() {
        return this.userTier;
    }
    //#endregion
}
